/**
 * 主應用組件
 * 組合子組件和整體布局，使用 hooks 和 contexts 管理狀態
 */

import * as React from "react";
import PropTypes from "prop-types";
import { makeStyles, Spinner, tokens } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import GroupTree from "./GroupTree";
import SearchSection from "./SearchSection";
import OrgSelector from "./OrgSelector";
import LoginPrompt from "./LoginPrompt";
import { AuthProvider, useAuthContext } from "../contexts/AuthContext";
import { AppProvider, useAppContext } from "../contexts/AppContext";
import { useAuth } from "../hooks/useAuth";
import { useApi } from "../hooks/useApi";
import { useGroups } from "../hooks/useGroups";
import { useOrgSelection } from "../hooks/useOrgSelection";
import { useRecipients } from "../hooks/useRecipients";
import { fetchCurrentUser } from "../services/userService";
import { getCurrentAccount } from "../services/authService";
import { loginWithMsal } from "../services/authService";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  content: {
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  treeWrapper: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    maxHeight: "75vh",
    overflow: "hidden",
  },
  spinnerContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "40px",
    flex: 1,
    minHeight: 0,
  },
});

const AppContent = ({ title }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const auth = useAuthContext();
  const { adapter } = useAppContext();
  
  const [searchQuery, setSearchQuery] = React.useState("");
  const [currentUser, setCurrentUser] = React.useState(null);
  const dialogRef = React.useRef(null);

  // 使用 hooks
  const apiCall = useApi(auth.getAccessToken, auth.tokenRef);
  const { selectedOrg, setSelectedOrg, setDefaultOrgFromUser } = useOrgSelection(currentUser);
  const { groups, treeData, loading, fetchGroups } = useGroups(selectedOrg, auth.getAccessToken, auth.tokenRef, apiCall);

  // 初始化：獲取 token 並獲取當前用戶資訊
  React.useEffect(() => {
    const initializeUser = async () => {
      const token = await auth.initializeAuth();
      if (token) {
        try {
          const userData = await fetchCurrentUser(auth.getAccessToken, auth.tokenRef);
          if (userData) {
            setCurrentUser(userData);
            setDefaultOrgFromUser(userData);
          }
        } catch (error) {
          console.error("Error fetching current user:", error);
        }
      }
    };

    initializeUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 當 selectedOrg 改變時，獲取群組
  React.useEffect(() => {
    if (selectedOrg && !auth.needsManualLogin) {
      fetchGroups();
    }
  }, [selectedOrg, fetchGroups, auth.needsManualLogin]);

  // 手動登入處理（擴展原有的 handleManualLogin）
  const handleManualLogin = React.useCallback(async () => {
    await auth.handleManualLogin();
    // 登入成功後獲取用戶資訊並重新載入群組
    if (!auth.needsManualLogin) {
      try {
        const userData = await fetchCurrentUser(auth.getAccessToken, auth.tokenRef);
        if (userData) {
          setCurrentUser(userData);
          setDefaultOrgFromUser(userData);
        }
        await fetchGroups();
      } catch (error) {
        console.error("Error after manual login:", error);
      }
    }
  }, [auth, fetchGroups, setDefaultOrgFromUser]);

  // 發送 token 到對話框
  const sendTokenToDialog = React.useCallback(async () => {
    if (!dialogRef.current) return;
    const token = await auth.getAccessToken();
    if (token) {
      dialogRef.current.messageChild(JSON.stringify({ type: "token", token }));
    }
  }, [auth]);

  // 發送目前情境（Mail / Calendar）到對話框
  const sendContextToDialog = React.useCallback(() => {
    if (!dialogRef.current) return;
    try {
      const itemType = Office?.context?.mailbox?.item?.itemType;
      const appointmentType = Office?.MailboxEnums?.ItemType?.Appointment;
      const isCalendarMode = !!(itemType && appointmentType && itemType === appointmentType);
      dialogRef.current.messageChild(JSON.stringify({ type: "context", isCalendarMode }));
    } catch (e) {
      dialogRef.current.messageChild(JSON.stringify({ type: "context", isCalendarMode: false }));
    }
  }, []);

  // 開啟搜尋對話框
  const openSearchDialog = React.useCallback(async () => {
    const token = await auth.getAccessToken(); 
    if (!token) {
      console.error("Cannot open search dialog without access token.");
      return;
    }

    const baseUrl = `${window.location.origin}/taskpane.html`;
    const queryParam = searchQuery ? `&q=${encodeURIComponent(searchQuery)}` : "";
    const url = `${baseUrl}?dialog=search${queryParam}`;

    Office.context.ui.displayDialogAsync(
      url,
      { height: 60, width: 60, displayInIframe: true },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error("Failed to open search dialog:", asyncResult.error.message);
          return;
        }

        dialogRef.current = asyncResult.value;

        const handleMessage = (args) => {
          try {
            const payload = JSON.parse(args.message);
            if (payload.type === "requestToken") {
              sendTokenToDialog();
              sendContextToDialog();
              return;
            }
            if (payload.type === "searchResults" && Array.isArray(payload.recipients) && payload.messageType) {
              // 使用適配器新增收件人
              const recipients = payload.recipients.map(item => ({
                displayName: item.displayName,
                emailAddress: item.mail || item.emailAddress || item.userPrincipalName,
              })).filter(r => r.emailAddress);
              
              adapter.addRecipients(recipients, payload.messageType).catch(error => {
                console.error("Failed to add recipients:", error);
              });
              
              dialogRef.current?.close();
              dialogRef.current = null;
              return;
            }
            if (payload.type === "cancel") {
              dialogRef.current?.close();
              dialogRef.current = null;
              return;
            }
          } catch (error) {
            console.error("Failed to parse dialog message:", error);
          }
        };

        const handleDialogEvent = () => {
          dialogRef.current = null;
        };

        dialogRef.current.addEventHandler(Office.EventType.DialogMessageReceived, handleMessage);
        dialogRef.current.addEventHandler(Office.EventType.DialogEventReceived, handleDialogEvent);
        
        // 先送 token + context，避免 Dialog 端因為拿不到 mailbox.item 而無法判斷模式
        sendTokenToDialog();
        sendContextToDialog();
      }
    );
  }, [searchQuery, sendTokenToDialog, sendContextToDialog, auth, adapter]);

  // 處理收件人新增（從 GroupTree 調用）
  const handleAddRecipients = React.useCallback(async (selectedGroupItems, memberToAdd, messageType) => {
    // 將選中的群組物件和成員物件合併
    const itemsToAdd = [...selectedGroupItems, ...memberToAdd];
    
    // 使用適配器新增收件人
    const recipients = itemsToAdd
      .map((item) => ({
        displayName: item.displayName,
        emailAddress: item.mail || item.userPrincipalName,
      }))
      .filter((r) => r.emailAddress);

    // 去重
    const uniqueRecipientMap = new Map();
    recipients.forEach((r) => {
      const email = r.emailAddress;
      if (!uniqueRecipientMap.has(email)) {
        uniqueRecipientMap.set(email, r);
      }
    });

    const uniqueRecipients = Array.from(uniqueRecipientMap.values());

    if (uniqueRecipients.length === 0) {
      console.warn("No valid recipients found.");
      return;
    }

    try {
      await adapter.addRecipients(uniqueRecipients, messageType);
    } catch (error) {
      console.error("Failed to add recipients:", error);
    }
  }, [adapter]);

  // 清理對話框
  React.useEffect(() => {
    return () => {
      if (dialogRef.current) {
        dialogRef.current.close();
      }
    };
  }, []);

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        {/* Search Section */}
        <SearchSection
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          onOpenSearchDialog={openSearchDialog}
        />

        {/* Org Selection */}
        <OrgSelector
          selectedOrg={selectedOrg}
          onOrgChange={setSelectedOrg}
        />

        {/* Tree View or Login */}
        <div className={styles.treeWrapper}>
          {auth.needsManualLogin ? (
            <LoginPrompt
              onLogin={handleManualLogin}
              isLoggingIn={auth.isLoggingIn}
              loginError={auth.loginError}
            />
          ) : loading ? (
            <div className={styles.spinnerContainer}>
              <Spinner label={t('loading_groups')} size="large" />
            </div>
          ) : (
            <GroupTree 
              groups={treeData} 
              getAccessToken={auth.getAccessToken}
              onAddRecipients={handleAddRecipients}
            />          
          )}
        </div>
      </div>
    </div>
  );
};

const App = (props) => {
  return (
    <AuthProvider>
      <AppProvider>
        <AppContent {...props} />
      </AppProvider>
    </AuthProvider>
  );
};

App.propTypes = {
  title: PropTypes.string,
};

export default App;
